import React, { Component, useState } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { Outlet } from "react-router";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import LeaveModal from "./components/LeaveModal.tsx";

import AuthService from "./services/auth.service";

import Login from "./pages/Login.tsx";
import Home from "./pages/Home.tsx";
import AppointmentsList from "./pages/AppointmentsList.tsx";
import AddAppointments from "./pages/AddAppointments.tsx";
import ClientConfirmation from "./pages/ClientConfirmation.tsx";
import CancelAppointment from "./pages/CancelAppointment.tsx";
import ServiceCalendar from "./pages/ServiceCalendar.tsx";
import ServiceList from "./pages/ServiceList.tsx";
import Settings from "./pages/Settings.tsx";
import ServiceConfirmation from "./pages/ServiceConfirmation.tsx";
import ServiceCancel from "./pages/ServiceCancel.tsx";

type CurrentUser = {
  dyno_enabled?: boolean;
};

type NavBarProps = {
  currentUser?: CurrentUser;
  logOut: () => void;
};

type AppState = {
  currentUser: CurrentUser | undefined;
};

const NavBar = ({ currentUser, logOut }: NavBarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLeaves, setShowLeaves] = useState(() => {
    const saved = localStorage.getItem("showLeaves");
    return saved === null ? true : saved === "true";
  });
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    handleMobileMenuClose();
    logOut();
  };

  const handleShowLeavesChange = (checked: boolean) => {
    localStorage.setItem("showLeaves", String(checked));
    setShowLeaves(checked);
    window.dispatchEvent(new Event("showLeavesChanged"));
  };

  const handleLeaveChanged = () => {
    window.dispatchEvent(new Event("leavesUpdated"));
  };

  return (
    <>
      <nav className="navbar navbar-expand navbar-dark bg-dark app-navbar">
      <Link to={"/"} className="navbar-brand app-navbar-brand">
        <img
          alt=""
          src="https://lirp.cdn-website.com/a85f75f8/dms3rep/multi/opt/LayoutsDB67747D-C218-0AC4-C1B8-2184CDD25E0F.png_400-356w.png"
          width={70}
        />
      </Link>
      {currentUser ? (
        <div className="app-navbar-links d-flex align-items-center flex-grow-1">
          <div className="navbar-nav d-flex flex-row flex-wrap gap-3 align-items-center app-navbar-primary">
            {currentUser.dyno_enabled !== false && (
              <>
                <li className="nav-item">
                  <Link to="/dyno" className="nav-link">
                    Dyno
                  </Link>
                </li>
              </>
            )}
            <li className="nav-item">
              <Link to="/service" className="nav-link">
                Service
              </Link>
            </li>
          </div>
          <div className="navbar-nav d-flex flex-row align-items-center gap-2 ms-auto app-navbar-actions">
            <li className="nav-item d-flex align-items-center me-2">
              <Form.Check
                type="checkbox"
                id="show-leaves-checkbox-nav"
                label="Technical Leaves"
                checked={showLeaves}
                onChange={(e) => handleShowLeavesChange(e.target.checked)}
                className="text-white me-2 mb-0"
                style={{ cursor: "pointer" }}
              />
            </li>
            <li className="nav-item me-2">
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowLeaveModal(true)}
              >
                + Add Leave
              </Button>
            </li>
            <li className="nav-item">
              <Link
                to="/settings"
                className="nav-link d-flex align-items-center"
                aria-label="Settings"
                title="Settings"
              >
                <span aria-hidden="true" style={{ fontSize: "1.2rem" }}>
                  ⚙
                </span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/login" className="nav-link" onClick={handleLogout}>
                LogOut
              </Link>
            </li>
          </div>
          <div className="app-navbar-mobile-menu-wrapper ms-auto">
            <button
              type="button"
              className="app-navbar-mobile-menu-toggle"
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((open) => !open)}
            >
              <span aria-hidden="true">☰</span>
            </button>
            {isMobileMenuOpen && (
              <div className="app-navbar-mobile-menu">
                <Link
                  to="/settings"
                  className="app-navbar-mobile-menu-link"
                  onClick={handleMobileMenuClose}
                >
                  Settings
                </Link>
                <Link
                  to="/login"
                  className="app-navbar-mobile-menu-link"
                  onClick={handleLogout}
                >
                  LogOut
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="navbar-nav ms-auto app-navbar-actions">
          <li className="nav-item">
            <Link to={"/login"} className="nav-link">
              Login
            </Link>
          </li>
        </div>
      )}
    </nav>
    <LeaveModal
      show={showLeaveModal}
      onHide={() => setShowLeaveModal(false)}
      onLeaveChanged={handleLeaveChanged}
    />
    </>
  );
};

const OurOutlet = () => {
  return (
    <div className="container mt-3">
      <Outlet />
    </div>
  );
};

const WithNavbar = ({ currentUser, logOut }: NavBarProps) => {
  return (
    <>
      <NavBar currentUser={currentUser} logOut={logOut} />
      <OurOutlet />
    </>
  );
};

const WithoutNavbar = () => {
  return (
    <>
      <OurOutlet />
    </>
  );
};

class App extends Component<Record<string, never>, AppState> {
  constructor(props: Record<string, never>) {
    super(props);
    this.logOut = this.logOut.bind(this);

    this.state = {
      currentUser: undefined,
    };
  }

  componentDidMount() {
    const user = AuthService.getCurrentUser();

    if (user) {
      this.setState({
        currentUser: user,
      });
    }
  }

  logOut() {
    AuthService.logout();
    this.setState({
      currentUser: undefined,
    });
  }

  render() {
    const { currentUser } = this.state;

    return (
      <div>
        <Routes>
          <Route element={<WithoutNavbar />}>
            <Route path="/login" element={<Login />} />
            <Route path="/confirm/:id" element={<ClientConfirmation />} />
            <Route path="/cancel/:id" element={<CancelAppointment />} />
            <Route
              path="/service/confirm/:id"
              element={<ServiceConfirmation />}
            />
            <Route
              path="/service/cancel/:id"
              element={<ServiceCancel />}
            />
          </Route>

          <Route
            element={
              <WithNavbar currentUser={currentUser} logOut={this.logOut} />
            }
          >
            <Route
              path="/"
              element={
                currentUser ? (
                  <Navigate to="/dyno" replace={true} />
                ) : (
                  <Navigate to="/login" replace={true} />
                )
              }
            />
            <Route
              path="/home"
              element={<Navigate to="/dyno" replace={true} />}
            />
            <Route path="/dyno" element={<Home />} />
            <Route path="/add" element={<AddAppointments />} />
            <Route path="/list" element={<AppointmentsList />} />
            <Route path="/service" element={<ServiceCalendar />} />
            <Route path="/service/list" element={<ServiceList />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Routes>
      </div>
    );
  }
}

export default App;
